import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { 
  GraduationCap, 
  Plus, 
  Edit, 
  Trash2, 
  Calendar,
  MapPin,
  Award,
  X
} from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';

const EducationManagement = () => {
  const navigate = useNavigate();
  const [educations, setEducations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue
  } = useForm();

  useEffect(() => {
    fetchEducations();
  }, []);

  const fetchEducations = async () => {
    try {
      const response = await api.get('/api/profile/education/');
      console.log('Education response:', response.data);
      
      // Handle different response structures
      const educationsData = Array.isArray(response.data) ? response.data : response.data?.results || [];
      setEducations(educationsData);
    } catch (error) {
      toast.error('Failed to fetch education records');
      console.error('Error fetching educations:', error);
      // Set empty array as fallback
      setEducations([]);
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data) => {
    try {
      if (editingId) {
        await api.patch(`/api/profile/education/${editingId}/`, data);
        toast.success('Education updated successfully');
      } else {
        await api.post('/api/profile/education/', data);
        toast.success('Education added successfully');
      }
      
      reset();
      setShowForm(false);
      setEditingId(null);
      fetchEducations();
    } catch (error) {
      toast.error(editingId ? 'Failed to update education' : 'Failed to add education');
      console.error('Error saving education:', error);
    }
  };

  const handleEdit = (education) => {
    setEditingId(education.id);
    setValue('institution', education.institution);
    setValue('degree', education.degree);
    setValue('field_of_study', education.field_of_study);
    setValue('start_date', education.start_date);
    setValue('end_date', education.end_date);
    setValue('gpa', education.gpa);
    setValue('description', education.description);
    setValue('is_current', education.is_current);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this education record?')) {
      return;
    }

    try {
      await api.delete(`/api/profile/education/${id}/`);
      toast.success('Education deleted successfully');
      fetchEducations();
    } catch (error) {
      toast.error('Failed to delete education');
      console.error('Error deleting education:', error);
    }
  };

  const handleCancel = () => {
    reset();
    setShowForm(false);
    setEditingId(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h1 className="text-2xl font-bold text-gray-900">Education</h1>
              <button
                onClick={() => setShowForm(true)}
                className="btn-primary flex items-center space-x-2"
              >
                <Plus className="w-5 h-5" />
                <span>Add Education</span>
              </button>
            </div>
          </div>

          <div className="p-6">
            {educations.length === 0 ? (
              <div className="text-center py-12">
                <GraduationCap className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No education records</h3>
                <p className="mt-1 text-sm text-gray-500">Add your educational background to showcase your qualifications.</p>
                <div className="mt-6">
                  <button
                    onClick={() => setShowForm(true)}
                    className="btn-primary"
                  >
                    Add Education
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                {Array.isArray(educations) && educations.map((education) => (
                  <div key={education.id} className="border border-gray-200 rounded-lg p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3">
                          <div className="p-2 bg-blue-100 rounded-lg">
                            <GraduationCap className="w-6 h-6 text-blue-600" />
                          </div>
                          <div>
                            <h3 className="text-lg font-medium text-gray-900">
                              {education.degree} in {education.field_of_study}
                            </h3>
                            <p className="text-sm text-gray-600">{education.institution}</p>
                          </div>
                        </div>
                        
                        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="flex items-center text-sm text-gray-500">
                            <Calendar className="w-4 h-4 mr-2" />
                            {education.start_date} - {education.is_current ? 'Present' : education.end_date}
                          </div>
                          {education.gpa && (
                            <div className="flex items-center text-sm text-gray-500">
                              <Award className="w-4 h-4 mr-2" />
                              GPA: {education.gpa}
                            </div>
                          )}
                        </div>
                        
                        {education.description && (
                          <p className="mt-3 text-sm text-gray-700">{education.description}</p>
                        )}
                      </div>
                      
                      <div className="flex items-center space-x-2 ml-4">
                        <button
                          onClick={() => handleEdit(education)}
                          className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                          title="Edit education"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(education.id)}
                          className="p-2 text-red-400 hover:text-red-600 transition-colors"
                          title="Delete education"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Education Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {editingId ? 'Edit Education' : 'Add Education'}
                </h3>
                <button
                  onClick={handleCancel}
                  className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Institution *
                    </label>
                    <input
                      {...register('institution', { required: 'Institution is required' })}
                      type="text"
                      className={`input-field ${errors.institution ? 'border-red-300 focus:ring-red-500' : ''}`}
                      placeholder="University of California"
                    />
                    {errors.institution && (
                      <p className="mt-1 text-sm text-red-600">{errors.institution.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Degree *
                    </label>
                    <select
                      {...register('degree', { required: 'Degree is required' })}
                      className={`input-field ${errors.degree ? 'border-red-300 focus:ring-red-500' : ''}`}
                    >
                      <option value="">Select degree</option>
                      <option value="High School">High School</option>
                      <option value="Associate">Associate</option>
                      <option value="Bachelor">Bachelor</option>
                      <option value="Master">Master</option>
                      <option value="Doctorate">Doctorate</option>
                      <option value="Certificate">Certificate</option>
                      <option value="Diploma">Diploma</option>
                    </select>
                    {errors.degree && (
                      <p className="mt-1 text-sm text-red-600">{errors.degree.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Field of Study *
                    </label>
                    <input
                      {...register('field_of_study', { required: 'Field of study is required' })}
                      type="text"
                      className={`input-field ${errors.field_of_study ? 'border-red-300 focus:ring-red-500' : ''}`}
                      placeholder="Computer Science"
                    />
                    {errors.field_of_study && (
                      <p className="mt-1 text-sm text-red-600">{errors.field_of_study.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      GPA
                    </label>
                    <input
                      {...register('gpa')}
                      type="text"
                      className="input-field"
                      placeholder="3.8"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Start Date *
                    </label>
                    <input
                      {...register('start_date', { required: 'Start date is required' })}
                      type="date"
                      className={`input-field ${errors.start_date ? 'border-red-300 focus:ring-red-500' : ''}`}
                    />
                    {errors.start_date && (
                      <p className="mt-1 text-sm text-red-600">{errors.start_date.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      End Date
                    </label>
                    <input
                      {...register('end_date')}
                      type="date"
                      className="input-field"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    {...register('description')}
                    rows={3}
                    className="input-field"
                    placeholder="Describe your education, achievements, or relevant coursework..."
                  />
                </div>

                <div className="flex items-center">
                  <input
                    {...register('is_current')}
                    type="checkbox"
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label className="ml-2 text-sm text-gray-700">
                    I am currently studying here
                  </label>
                </div>

                <div className="flex justify-end space-x-4 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn-primary"
                  >
                    {editingId ? 'Update Education' : 'Add Education'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EducationManagement;
